import React, { useContext, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import { Box, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import APIContext from "../../../services/api/api";
import { URLS } from "../../../configuration/urls";

AnnotationsModule(Highcharts);

export function ThroughputChart({ target = 2160, today }) {
  const [throughputData, setThroughputData] = useState([]);
  const [deviationData, setDeviationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getData } = useContext(APIContext);
  const { formatMessage } = useIntl();

  useEffect(() => {
    async function fetchAllData() {
      setIsLoading(true);
      try {
        const [throughput, deviation] = await Promise.all([
          getData(`h7/kpi/throughput?currentDate=${today}`, {}, URLS.EC_CLEAR_CUSTOMS_BE()),
          getData(`h7/kpi/throughput-deviation?currentDate=${today}`, {}, URLS.EC_CLEAR_CUSTOMS_BE())
        ]);
        setThroughputData(throughput);
        setDeviationData(deviation);
      } catch (error) {
        console.log("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllData();
  }, []);


  const categories = Array.isArray(deviationData) ? throughputData?.map(item => {
    switch (item.type) {
      case "day":
        return "";
      case "week":
        return "";
      case "month":
        return "";
      case "quarter":
        return "";
      default:
        return item.type;
    }
  }) : [];

  const actualValues = Array.isArray(throughputData) ? throughputData?.map(item => item?.throughput || "") : [];
  const targetValues = Array.isArray(throughputData) ? throughputData?.map(() => target || "") : [];
  const deviationPercentages = Array.isArray(deviationData) ? deviationData?.map(item => item?.deviation) : [];
  const annotations = Array.isArray(throughputData) ? throughputData?.map((item, i) => {
    const deviation = deviationPercentages?.[i];
    if (deviation === undefined) return {};
    return ({
      labels: [
        {
          shape: "rect",
          allowOverlap: false,
          point: {
            xAxis: 0,
            yAxis: 0,
            x: i,
            y: target
          },
          text: `${deviation > 0 ? "+" : ""}${deviation}%`,
          backgroundColor: "rgba(0,0,0,0)",
          borderWidth: 0,
          style: {
            color: "var(--button-subtle-text)",
            fontWeight: "bold",
            fontSize: "14px",
            textOutline: "none"
          },
          y: -1.5,
          x: -55
        }
      ]
    });

  }) : [];

  function drawConnectorLines(chart) {
    if (!Array.isArray(throughputData)) return;
    if (chart._connectorLines) {
      chart._connectorLines.forEach(el => el.destroy());
    }
    chart._connectorLines = [];
    throughputData.forEach((_, i) => {
      const deviation = deviationPercentages?.[i];
      if (deviation === undefined) return;
      const px = chart.xAxis[0].toPixels(i, false) - 20;
      const py = chart.yAxis[0].toPixels(target, false);
      const bx = px - 6;
      const by = py - 8;
      const ex = bx - 50;
      const ey = by;
      const el = chart.renderer.path(["M", px, py, "L", bx, by, "L", ex, ey])
        .attr({ stroke: "var(--warning-warning-160)", "stroke-width": 1.5, zIndex: 5 })
        .add();
      chart._connectorLines.push(el);
    });
  }

  const options = {
    chart: {
      type: "column",
      height: Array.isArray(deviationData) ? 400 : 100,
      events: {
        render() { drawConnectorLines(this); }
      }
    },
    title: { text: null },
    xAxis: {
      categories,
      borderWidth: 100,
      lineWidth: 4,
      lineColor: "var(--text-secondary)"
    },
    yAxis: {
      min: 0,
      title: { text: "Minutes" },
      visible: false
    },
    tooltip: {
      enabled: false
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { fontWeight: "bold", fontSize: "20px", color: "var(--font-medium-default)" }
    },
    credits: { enabled: false },
    plotOptions: {
      column: {
        grouping: false,
        allowPointSelect: true,
        borderWidth: 0
      }
    },
    series: [
      {
        name: `<span style=\"font-size:20px;\">${formatMessage({ id: "accumulatedAverageThroughputTime" })}</span> <span style=\"font-size:14px;\">(${formatMessage({ id: "minutes" })})</span>`,
        data: actualValues.length > 0 ? actualValues : [0],
        color: "var(--warning-warning-40)",
        fontSize: 23,
        pointWidth: 100,
        dataLabels: {
          enabled: true,
          distance: 40,
          style: {
            fontWeight: "bold",
            fontSize: "16px",
            textOutline: "0px none",
            color: "var(--font-strong-default)"
          },
          inside: false,
          verticalAlign: "bottom",
          y: 30
        }
      },
      {
        name: `<span style=\"font-size:20px;\">${formatMessage({ id: "deviationFromTarget" })}</span> <span style=\"font-size:14px;\">(${target} ${formatMessage({ id: "minutes" })})</span>`,
        data: targetValues.length > 0 ? targetValues : [0],
        color: "var(--warning-warning-80)",
        pointWidth: 80,
        borderWidth: 0
      }
    ],
    annotations: annotations
  };
  return (
    <Box sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 4px -2px var(--10), 0 4px 6px -1px var(--10)", border: "solid 1px var(--new-border)", backgroundColor: "var(--app-body)" }}>
      <Box
        sx={{
          height: "48px",
          flexGrow: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "solid 1px var(--new-border)"
        }}
      >
        <Typography
          data-testid={`throughput_box_heading`}
          color={"var(--text-primary)"} variant="subtitle1" fontWeight={600}
          fontSize={"20px"}
          sx={{
            height: "28px",
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "8px",
            padding: 0
          }}
        >
          <FormattedMessage id="accumulatedAverageThroughputTime" />
          {" "}(<FormattedMessage id="minutes" />)
        </Typography>
      </Box>
      {!Array.isArray(deviationData) &&
        <Typography
          style={{
            textAlign: "center",
            padding: "3rem"
          }}
          variant="subtitle1"
        >
          <FormattedMessage id="noData" />
        </Typography>
      }
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}

